import { getReviews, getReviewsPublicFromParents, postReviews, postReviewsQA } from '@app/packages/api/v2/actions/reviews'
import type {
  GetReviewsPublicFromParentsQuery,
  GetReviewsQuery,
  RequestReviewCreateRequest,
  RequestReviewQACreateRequest,
} from '@app/packages/api/v2/types/params'

import { updateAnonymousParentsSlice } from '@app/store/slices/anonymous_parents'
import {
  addPublicParentReviewsPageSlice,
  failPublicParentReviewsPageSlice,
  publicParentReviewsMetaSelector,
  startPublicParentReviewsPageSlice,
} from '@app/store/slices/public_parent_reviews'
import { markRequestReviewedSlice } from '@app/store/slices/requests_models'
import { addReviewsPageSlice, failReviewsPageSlice, reviewsMetaSelector, reviewsQuerySelector, startReviewsPageSlice } from '@app/store/slices/reviews'
import { setUsersFromResponseSlice } from '@app/store/slices/users'
import { createThunk } from '@app/store/thunk'

import { callV2Api } from './api'

export const getInitialPublicParentReviews = () =>
  createThunk(dispatch => dispatch(readPublicParentReviews({ page: 1, per_page: 10, 'filter[length_gteq]': 50, 'filter[length_lteq]': 250 })))

export const getNextPublicParentReviews = () =>
  createThunk((dispatch, getState) => {
    const meta = publicParentReviewsMetaSelector(getState())
    if (meta.fetching || meta.fetched_all) return
    return dispatch(readPublicParentReviews({ page: meta.page + 1, per_page: 10, 'filter[length_gteq]': 50, 'filter[length_lteq]': 250 }))
  })

export function fetchReviews(user_token: string) {
  return readReviews({ token: user_token, page: 1, per_page: 10 })
}

export function fetchNextReviews() {
  return createThunk((dispatch, getState) => {
    const storeMeta = reviewsMetaSelector(getState())
    const query = reviewsQuerySelector(getState())

    return dispatch(readReviews({ token: query.token, page: (storeMeta.current_page || 0) + 1, per_page: 10 }))
  })
}

/** The request the review was left on carries the flag the lists read, so it is stamped here. */
export function createReview(request: RequestReviewCreateRequest, reviewer: 'sitter' | 'parent') {
  return createThunk(async dispatch => {
    const resp = await dispatch(callV2Api(postReviews(request)))
    dispatch(markRequestReviewedSlice(request.request_id, reviewer))
    return resp
  })
}

export function createReviewQA(request: RequestReviewQACreateRequest) {
  return createThunk(async dispatch => await dispatch(callV2Api(postReviewsQA(request))))
}

function readReviews(query: GetReviewsQuery) {
  return createThunk(async dispatch => {
    dispatch(startReviewsPageSlice())
    try {
      const resp = await dispatch(callV2Api(getReviews(query)))
      dispatch(addReviewsPageSlice(query, resp))
      return resp
    } catch (e) {
      dispatch(failReviewsPageSlice())
      throw e
    }
  })
}

/** The response carries whole sitters and the anonymous parents who wrote the reviews. */
function readPublicParentReviews(query: GetReviewsPublicFromParentsQuery) {
  return createThunk(async dispatch => {
    dispatch(startPublicParentReviewsPageSlice())
    try {
      const resp = await dispatch(callV2Api(getReviewsPublicFromParents(query)))
      dispatch(addPublicParentReviewsPageSlice(query, resp))
      dispatch(updateAnonymousParentsSlice(resp))
      dispatch(setUsersFromResponseSlice(resp))
      return resp
    } catch (e) {
      dispatch(failPublicParentReviewsPageSlice())
      throw e
    }
  })
}
